import React, { useMemo } from 'react';
import { ReactSVG } from 'react-svg';
import { alpha, Box, styled, useTheme } from '@mui/material';

import { Typography } from '@zendesk/forethought-elements';
import {
  getWorkflowIdByAnyChannel,
  getWorkflowIdByChannel,
} from '../workflow-builder/intent-workflows-table/helper';
import { getWorkflowBuilderUrl, openTab } from './helpers';
import {
  useGetAllIntentsQuery,
  useGetIntentsQueryWithProduct,
  useIsFeatureFlagEnabled,
} from 'src/hooks/hooks';
import buildingBlockIcon from 'src/pages/workflow-builder-edit/assets/icons/building-block.svg';
import { SolveWidgetProduct } from 'src/types/types';
import {
  ConversationChannel,
  IntentData,
} from 'src/types/workflowBuilderAPITypes';
import {
  getEffectiveWorkflowMode,
  isIntentActiveForChannel,
} from 'src/utils/solve/intentUtils';
type IntentState = 'active' | 'not-active' | 'error';

const useGetColorFromIntentState = (intentState: IntentState) => {
  const { palette } = useTheme();

  switch (intentState) {
    case 'not-active':
      return palette.colors.grey[400];
    case 'active':
      return palette.colors.green[500];
    case 'error':
      return palette.colors.red[500];
    default:
      return palette.colors.red[500];
  }
};

const getIntentState = (
  intentData: IntentData | undefined,
  channel: ConversationChannel,
): IntentState => {
  if (!intentData) {
    return 'error';
  }

  return isIntentActiveForChannel(intentData.active_workflow_types, channel)
    ? 'active'
    : 'not-active';
};

export default function IntentBadge({
  alwaysClickable = false,
  channel = 'widget',
  defaultStatusColor,
  emailConfigurationId,
  emailMode,
  intentDefinitionId,
  intentName,
  intentNameSuffix,
  intentWorkflowId,
  isOnHeader = false,
  noIntentLabel = 'No intent detected',
  noWrap = true,
  onClick,
  productOverride,
  showBuildingBlockIconWhenUnresolved = false,
}: {
  // When true, the badge stays clickable (firing `onClick`) even if the intent
  // can't be resolved — for callers that own the click semantics (e.g. opening a
  // menu / expanding a row) rather than deeplinking to the intent itself.
  alwaysClickable?: boolean;
  channel?: ConversationChannel;
  defaultStatusColor?: IntentState;
  emailConfigurationId?: string | null;
  emailMode?: string;
  intentDefinitionId?: string | null;
  intentName?: string | null;
  intentNameSuffix?: React.ReactNode;
  intentWorkflowId?: string;
  isOnHeader?: boolean;
  noIntentLabel?: string;
  noWrap?: boolean;
  onClick?: React.MouseEventHandler<HTMLDivElement>;
  productOverride?: SolveWidgetProduct;
  // Opt-in: when the intent can't be resolved, show the building-block glyph
  // instead of the red "error" dot. Autoflow building blocks are publish-time
  // snapshot intents that are deliberately absent from /intents, so they can
  // never resolve here — red reads as a failure when nothing is actually wrong.
  // Only callers whose unresolved case is genuinely a building block should set
  // this; everywhere else an unresolved intent stays red.
  showBuildingBlockIconWhenUnresolved?: boolean;
}) {
  const { palette } = useTheme();
  const isClassicWorkflowsDeprecated = useIsFeatureFlagEnabled(
    'classic_workflows_deprecated',
  );
  const { data: allIntentsData } = useGetAllIntentsQuery({
    skip: Boolean(productOverride),
  });
  const { data: intentsQueryWithProductData } = useGetIntentsQueryWithProduct(
    undefined,
    {
      productOverride,
      skip: !productOverride,
    },
  );
  const intents = intentsQueryWithProductData?.intents || allIntentsData;

  const intentDetails = useMemo(() => {
    return intents.find(
      intent =>
        intent.intent_definition_id === intentDefinitionId ||
        getWorkflowIdByChannel(channel, intent) === intentWorkflowId ||
        getWorkflowIdByAnyChannel(intent) === intentWorkflowId,
    );
  }, [intents, intentDefinitionId, intentWorkflowId, channel]);

  const shouldShowLink = Boolean(intentDetails);
  // Clickable when there's something to deeplink to, or when the caller owns the
  // click semantics (alwaysClickable) regardless of intent resolution.
  const isClickable = shouldShowLink || alwaysClickable;
  const usableIntentName = intentName || intentDetails?.intent_name || '';

  const statusColor = useGetColorFromIntentState(
    defaultStatusColor || getIntentState(intentDetails, channel),
  );
  // An explicit defaultStatusColor is a deliberate caller override, so it wins.
  const shouldShowBuildingBlockIcon =
    showBuildingBlockIconWhenUnresolved &&
    !intentDetails &&
    !defaultStatusColor;

  const bgcolor = usableIntentName
    ? palette.colors.slate[100]
    : alpha('#FFD8CC', 0.3);

  const handleClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (!intentDetails) {
      return;
    }
    e.stopPropagation();
    const { intent_definition_id } = intentDetails;
    if (emailConfigurationId) {
      const url = getWorkflowBuilderUrl({
        channel: 'email',
        emailConfigurationId: emailConfigurationId,
        emailMode: emailMode,
        intentDefinitionId: intent_definition_id,
        intentWorkflowId: intentDetails.intent_workflow_id ?? '',
      });
      openTab(url);
    } else {
      const url = getWorkflowBuilderUrl({
        channel,
        emailMode: channel == 'email' ? emailMode : undefined,
        intentDefinitionId: intent_definition_id,
        intentWorkflowId: getWorkflowIdByChannel(channel, intentDetails) ?? '',
        mode: getEffectiveWorkflowMode(
          intent_definition_id,
          intentDetails.is_autoflow,
          isClassicWorkflowsDeprecated,
        ),
      });
      openTab(url);
    }
  };

  return (
    <Box
      component='div'
      onClick={isClickable ? (onClick ?? handleClick) : undefined}
      sx={[
        {
          alignItems: 'center',
          bgcolor: bgcolor,
          borderRadius: '4px',
          display: 'flex',
          gap: isOnHeader ? 1 : 0.5,
          maxWidth: '100%',
          overflow: 'hidden',
          padding: isOnHeader ? '6.25px 12px' : '4px 8px',
          width: 'fit-content',
        },
        isClickable
          ? {
              '.MuiTypography-root': {
                lineHeight: 'initial',
                textDecoration: 'underline',
                textDecorationThickness: '1px',
              },
              ':hover': {
                bgcolor: palette.colors.slate[200],
                cursor: 'pointer',
              },
            }
          : {},
      ]}
    >
      <Box
        sx={{
          alignItems: 'center',
          display: 'flex',
          height: isOnHeader ? '21px' : '18px',
        }}
      >
        {shouldShowBuildingBlockIcon ? (
          <BuildingBlockStatusIcon src={buildingBlockIcon} />
        ) : (
          <StatusIcon color={statusColor} />
        )}
      </Box>
      <Typography
        color={palette.colors.grey[700]}
        noWrap={noWrap}
        variant={isOnHeader ? 'font20' : 'font12Medium'}
      >
        {usableIntentName || noIntentLabel}
      </Typography>
      {intentNameSuffix && intentNameSuffix}
    </Box>
  );
}

const StatusIcon = styled('div')<{ color: string }>`
  height: 8px;
  width: 8px;
  flex: 0 0 8px;
  border-radius: 50%;
  background-color: ${props => props.color};
`;

// 14px rather than the 8px status dot: the glyph is three stacked layers and is
// illegible at dot size. Matches the inline building-block icon in the autoflow
// editor (InlineBuildingBlockNodeDisplay), including its color, so the same
// concept looks the same in both places.
// The asset hardcodes stroke="white", so `path` and `*` both need overriding —
// `*` with !important to beat the inline stroke attribute.
const BuildingBlockStatusIcon = styled(ReactSVG)`
  display: flex;
  flex: 0 0 14px;

  svg {
    width: 14px;
    height: 14px;

    path {
      stroke: #61670b;
    }

    * {
      stroke: #61670b !important;
    }
  }
`;
